/**
 * Footer Commands Implementation for UI-Model-1
 *
 * Handles execute and undo operations for footer configuration updates.
 */

import { UIModelState } from '../../types/state.types';
import { UpdateFooterCommand } from '../../types/command.types';
import { FooterConfigurationAPI, DEFAULT_FOOTER_CONFIGURATION } from '../../types/api.types';

/**
 * Execute UPDATE_FOOTER command
 * Merges the updates into the current footer configuration.
 * If footer doesn't exist yet, initializes with defaults.
 */
export const executeUpdateFooter = (
  state: UIModelState,
  command: UpdateFooterCommand
): UIModelState => {
  if (!state.model) {
    console.warn('Cannot update footer: no model found');
    return state;
  }

  // Footer is optional on LayoutAPI — initialize with defaults if missing
  const currentConfig = state.model.layout.footer?.configuration ?? { ...DEFAULT_FOOTER_CONFIGURATION };

  // Store previous values for undo (mutate command for undo support)
  const previousValues: Partial<FooterConfigurationAPI> = {};
  for (const key of Object.keys(command.payload.updates) as (keyof FooterConfigurationAPI)[]) {
    previousValues[key] = currentConfig[key] as never;
  }
  command.payload.previousValues = previousValues;

  return {
    ...state,
    model: {
      ...state.model,
      layout: {
        ...state.model.layout,
        footer: {
          ...state.model.layout.footer,
          configuration: {
            ...currentConfig,
            ...command.payload.updates,
          },
        },
      },
    },
  };
};

/**
 * Undo UPDATE_FOOTER command
 * Restores the previous footer configuration values
 */
export const undoUpdateFooter = (
  state: UIModelState,
  command: UpdateFooterCommand
): UIModelState => {
  const currentConfig = state.model?.layout.footer?.configuration;
  if (!currentConfig || !state.model || !command.payload.previousValues) {
    console.warn('Cannot undo footer update: missing state or previous values');
    return state;
  }

  return {
    ...state,
    model: {
      ...state.model,
      layout: {
        ...state.model.layout,
        footer: {
          ...state.model.layout.footer,
          configuration: {
            ...currentConfig,
            ...command.payload.previousValues,
          },
        },
      },
    },
  };
};
